"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Payments = void 0;
const events_1 = require("./events");
const functions_1 = require("../functions");
class Payments extends events_1.Emitter {
    constructor(stripe, firebase) {
        super();
        this.stripe = stripe;
        this.firebase = firebase;
    }
    setCustomer(customer) {
        return (this.customer = functions_1.functions.payments.Customer.load(customer));
    }
    getCustomer() {
        return this.customer;
    }
    async setItem(item) {
        item.quantity = item.quantity || 1;
        if (item && this.item && item.id !== this.item.id) {
            // Item was changed
            //TODO: fireAnalytics("select_content", {...});
        }
        this.item = functions_1.functions.payments.PaymentItem.load(item);
        if (this.paymentRequest) {
            if (!this.paymentRequest.isShowing()) {
                // Can only update the paymentRequest if it isn't already showing
                this.paymentRequest.update({
                    total: {
                        label: this.item.label,
                        amount: this.item.amount * this.item.quantity,
                    },
                });
                const result = await this.paymentRequest.canMakePayment();
                this.emit('onPaymentRequestUpdate', result);
            }
            else {
                console.warn('An attempt was made to setItem while the paymentRequest dialogue was displayed.  This will likely result in an invalid paymentRequest.');
            }
        }
        else {
            console.log('Not updating paymentRequest');
        }
        return this.item;
    }
    getItem() {
        return this.item;
    }
    /**
     *
     * Initialize the payment system.  This is needed
     * primariliy to setup the PaymentRequest plumbing
     *
     */
    async init() {
        if (!this.paymentRequest) {
            this.paymentRequest = this.stripe.paymentRequest({
                country: 'US',
                currency: 'usd',
                total: this.item || { amount: 0, label: '' },
                requestPayerName: true,
                requestPayerEmail: true,
            });
            this.paymentRequest.on('paymentmethod', event => {
                this.submitPayment({
                    paymentMethod: event.paymentMethod.id,
                    paymentRequestEvent: event,
                });
            });
            const result = await this.paymentRequest.canMakePayment();
            this.emit('onPaymentRequestInit', result);
        }
    }
    /**
     * Submit a payment to Stripe.  Returns `true` if payment completed
     * successfully, `false` if there was a failure.
     *
     * For PaymentIntent flow details, see:
     *    https://stripe.com/docs/payments/intents#intent-statuses
     */
    async submitPayment(options) {
        const paymentMethod = options.paymentMethod;
        const paymentRequestEvent = options.paymentRequestEvent || null;
        if (options.item) {
            this.setItem(options.item);
        }
        if (options.customer) {
            this.setCustomer(options.customer);
        }
        const response = await this._submitPayment(paymentMethod, paymentRequestEvent);
        if (!response.success && paymentRequestEvent) {
            paymentRequestEvent.complete('fail');
        }
        if (response.success) {
            const { result, error } = await (0, functions_1.functions)(this.firebase).payments.finalizePayment({
                paymentIntentId: response.paymentIntent.id,
            });
            if (error || !result.success) {
                console.error('Error finalizing payment');
            }
            else {
                response.details = result.details;
            }
        }
        /*
        if (this.item && response.success) {
          //
          // TODO: Implement
          //
          /*
          fireAnalytics("purchase", {
            transaction_id: response?.context?.transactions?.[0]?.id,
            value: this.item.amount / 100,
            fb_tag: this.item.conversionTracking,
            twitter_tag: this.item.twitterTagId,
            currency: "USD",
            tax: null,
            shipping: null,
            items: [
              {
                id: this.item.id,
                name: this.item.label,
                brand: null,
                category: this.item.type,
                variant: this.item.conversionTracking,
                list_name: this.item.description,
                list_position: 1,
                quantity: 1,
                price: this.item.amount / 100,
              },
            ],
          });
        }
        */
        this.emit('onPaymentComplete', response);
        return response;
    }
    /**
     * @internal
     */
    async _submitPayment(paymentMethod, paymentRequestEvent) {
        var _a, _b;
        const result = {
            success: false,
        };
        console.log(`Submitting payment for item: ${JSON.stringify(this.item)}`);
        if (!this.item) {
            result.success = false;
            result.error = 'No item has been set';
            return result;
        }
        if (!this.customer) {
            result.success = false;
            result.error = 'No customer has been set';
            return result;
        }
        const response = await (0, functions_1.functions)(this.firebase).payments.initPayment({
            item: this.item,
            customer: this.customer,
        });
        if (response.error) {
            console.log('Error initializing payment', response.error);
            result.success = false;
            result.error = response.error.message;
            return result;
        }
        console.log('Got response from initPayment:', response.result);
        if (!response.result.success) {
            console.error('Error creating payment');
            // TODO: Use Stripe's error message
            result.success = false;
            result.error = response.result.error;
            return result;
        }
        if (response.result.purchase_complete) {
            console.log('Payment completed successfully');
            result.success = true;
            return result;
        }
        const clientSecret = response.result.token;
        console.log('Contacting Stripe...');
        const options = {
            payment_method: paymentMethod,
        };
        // If a paymentRequestEvent is provided, then we
        // need to tell Stripe that we will handle any further
        // actions ourselves (handleActions: false).  We need
        // to do this because we need to signal the
        // paymentRequestEvent before we can propmt the user
        // for additional action.
        let stripeResponse = await this.stripe
            .confirmCardPayment(clientSecret, options, {
            handleActions: paymentRequestEvent ? false : true,
        })
            .catch(error => {
            console.log('Error confirming payment with Stripe', error);
            return null;
        });
        if (stripeResponse) {
            console.log('Got response from Stripe');
            result.paymentIntent = stripeResponse.paymentIntent;
        }
        if (!stripeResponse || stripeResponse.error) {
            if (paymentRequestEvent) {
                console.log('Marking Payment Request fail');
                paymentRequestEvent.complete('fail');
                // We need to do this again to properly cleanup
                // the PaymentRequest process.
                await this.stripe.confirmCardPayment(clientSecret).catch(() => null);
            }
            result.success = false;
            result.error = ((_a = stripeResponse === null || stripeResponse === void 0 ? void 0 : stripeResponse.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown';
            return result;
        }
        // Things are looking good...
        if (stripeResponse.paymentIntent) {
            if (paymentRequestEvent) {
                console.log('Marking Payment Request success');
                paymentRequestEvent.complete('success');
            }
            //
            // This can return a 400 error because the payment is possibly already
            // complete.  However, we need to do this in case there is a verification
            // step required.  This is the official way to do this.
            //
            // See more:
            //  * https://stripe.com/docs/stripe-js/elements/payment-request-button#html-js-complete-payment
            //  * https://github.com/stripe/stripe-payments-demo/issues/133#issuecomment-632593669
            //
            stripeResponse = await this.stripe
                .confirmCardPayment(clientSecret)
                .catch(() => null);
            if (((_b = stripeResponse === null || stripeResponse === void 0 ? void 0 : stripeResponse.paymentIntent) === null || _b === void 0 ? void 0 : _b.status) === 'succeeded') {
                console.log('Payment completed successfully');
                result.success = true;
                return result;
            }
        }
        if (paymentRequestEvent) {
            console.log('Marking Payment Request fail');
            paymentRequestEvent.complete('fail');
        }
        result.success = false;
        result.error = 'Unknown';
        return result;
    }
}
exports.Payments = Payments;
//# sourceMappingURL=payments.js.map